import produce from 'immer'
import cloneDeep from 'lodash/cloneDeep'
import has from 'lodash/has'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import set from 'lodash/set'

import {
  ADD_LAYER,
  ADD_LAYERS,
  ADD_GROUPS_FROM_LAYERS,
  ADD_PROFILES_FROM_LAYERS,
  SET_GROUP_COLLAPSED,
  SET_GROUP_EXPANDED,
  SET_LAYER_FILTER,
  SET_LAYER_HIDDEN,
  SET_LAYER_ID,
  SET_LAYER_IN_DRAWER_HIDDEN,
  SET_LAYER_IN_DRAWER_VISIBLE,
  SET_LAYER_LOAD_DONE,
  SET_LAYER_LOAD_FAILED,
  SET_LAYER_LOAD_IN_PROGRESS,
  SET_LAYER_LOAD_NOT_STARTED,
  SET_LAYER_PANELS_CLOSED,
  SET_LAYER_PANELS_FEATURE_OPEN,
  SET_LAYER_PANELS_OPEN,
  SET_LAYER_REPORT,
  SET_LAYER_SELECTED,
  SET_LAYER_STYLE_GEOSTYLER,
  SET_LAYER_UNSELECTED,
  SET_LAYER_VISIBLE,
  SET_CURRENT_PROFILE
} from 'drawer/tabs/layers/actions'
import { getIdFromProfileName, sanitiseProfileName } from 'utils/profile'

// https://stackoverflow.com/a/57369255/1550587
// https://redux.js.org/recipes/structuringreducers/immutableupdatepatterns
export const groups = (state = null, action) => {
  const { layers, type } = action

  switch (type) {
    case ADD_GROUPS_FROM_LAYERS: {
      // Do not process if no layers have been provided
      if (!layers || layers?.length === 0) {
        return state
      }

      // console.debug('ADD_GROUPS_FROM_LAYERS - state from config', state.config)

      // Convert potential individual layers to an array
      const layerConfs = [].concat(layers)

      // Filter out layers without groups
      const groupLayers = layerConfs.filter(layerConf => layerConf.groupid)

      // Do not process if none of the layers have groups
      if (groupLayers.length === 0) {
        return state
      }

      return produce(state, groups => {
        // console.debug('ADD_GROUPS_FROM_LAYERS - groups add from layer', groupLayers)

        for (const layerConf of groupLayers) {
          const { group, groupid, titleid } = layerConf

          // console.debug('ADD_GROUPS_FROM_LAYERS - groups add from layer', titleid, group, groupid)

          if (!titleid) {
            console.warn('ADD_GROUPS_FROM_LAYERS - no titleid.', titleid, group, groupid, layerConf)
            continue
          }

          // Create new group if needed
          if (!groups[groupid]) {
            groups[groupid] = {
              expanded: null, // This will be set in setGroupsExpandedState in Groups.jsx
              id: groupid,
              layers: [],
              name: group
            }
          }

          // Do not add layer if already in group
          if (includes(groups[groupid].layers, titleid)) {
            continue
          }

          // console.debug('ADD_GROUPS_FROM_LAYERS - adding layer to group', groupid, titleid)

          groups[groupid].layers.push(titleid)
        }

        // console.debug('ADD_GROUPS_FROM_LAYERS - groups', { ...groups })

        return groups
      })
    }

    case SET_GROUP_COLLAPSED:
      return produce(state, groups => {
        // Handle singular groups as well as arrays of groups
        const groupsToCollapse = [].concat(action.group || [])
        for (const group of groupsToCollapse) {
          groups[group.id].expanded = false
        }
      })
    case SET_GROUP_EXPANDED:
      return produce(state, groups => {
        // Handle singular groups as well as arrays of groups
        const groupsToExpand = [].concat(action.group || [])
        for (const group of groupsToExpand) {
          groups[group.id].expanded = true
        }
      })
    default:
      return state
  }
}

export const currentProfile = (state = null, action) => {
  switch (action.type) {
    case SET_CURRENT_PROFILE:
      return action.profileId
    default:
      return state
  }
}

export const profiles = (state = null, action) => {
  switch (action.type) {
    case ADD_PROFILES_FROM_LAYERS:
      if (isNil(action.layers) || (action.layers instanceof Array && action.layers.length === 0)) {
        return state
      }

      return produce(state, profiles => {
        const layerConfs = [].concat(action.layers)

        for (const layerConf of layerConfs) {
          // console.debug('profiles add layer', layerConf.titleid, layerConf.profiles)

          const { profiles: layerProfileNames, titleid } = layerConf

          if (!titleid) {
            console.warn('profiles - ADD_PROFILES_FROM_LAYERS: no titleid.', layerConf)
            continue
          }

          if (!(layerProfileNames instanceof Array) || layerProfileNames.length === 0) {
            continue
          }

          for (const layerProfileName of layerProfileNames) {
            const profileName = sanitiseProfileName(layerProfileName)
            const profileId = profileName && getIdFromProfileName(profileName)

            // console.debug('profiles - ADD_PROFILES_FROM_LAYERS - layerProfiles - profile', profileName, profileId, titleid)

            if (!profileId) {
              console.warn(`profiles - ADD_PROFILES_FROM_LAYERS - invalid profile name: '${layerProfileName}' from layer '${titleid}'`)
              continue
            }

            let profile = profiles[profileId]

            // Create new profile if it did not exist yet
            if (!profile) {
              profile = {
                id: profileId,
                name: profileName,
                layers: []
              }

              profiles[profileId] = profile
            }

            // Add current layer to profile
            profile.layers.push(titleid)
          }
        }

        // console.debug('profiles - ADD_PROFILES - produce -layerProfiles', profiles)
      })
    default:
      return state
  }
}

// https://stackoverflow.com/questions/36031590/right-way-to-update-state-in-redux-reducers
// https://redux.js.org/recipes/structuringreducers/immutableupdatepatterns
export const layers = (state = null, action) => {
  switch (action.type) {
    case ADD_LAYER:
    case ADD_LAYERS:
      if (isNil(action.layers) || (action.layers instanceof Array && action.layers.length === 0)) {
        return state
      }
      // Bit of a waste here because this is a new object, but for consistency.
      return produce(state, layers => {
        // Make sure that we work on an array of layerConfs, even if only one
        // layerConf has been provided. This allows us to cater for one or more
        // added layerConfs
        const layerConfs = [].concat(action.layers)

        for (let layerConf of layerConfs) {
          // console.log('reduce layer', layerConf.titleid, layerConf)

          if (!layerConf.titleid) {
            console.warn('ADD_LAYER: no titleid.', layerConf)
            continue
          }

          // Make a deep copy of the layer. This is good practice and necessary to
          // avoid having an object that is (partially) read-only (i.e. frozen)
          layerConf = cloneDeep(layerConf)

          // Add defaults
          if (!has(layerConf, 'localDataSource')) {
            layerConf.localDataSource = false
          }

          if (!has(layerConf, 'selected')) {
            layerConf.selected = false
          }
          layerConf.isSelected = function () {
            return this.selected
          }

          // If a layer visibility has not been set explicitly, set it to false
          if (!has(layerConf, 'visible')) {
            layerConf.visible = false
          }

          layerConf.isVisible = function () {
            return this.visible
          }

          if (!has(layerConf, 'panelsOpen')) {
            layerConf.panelsOpen = false
          }

          if (!has(layerConf, 'panelFeatureTitle')) {
            layerConf.panelFeatureTitle = ''
          }

          if (!has(layerConf, 'minZoom')) {
            layerConf.minZoom = -1
          }

          if (!has(layerConf, 'loadstatus')) {
            layerConf.loadstatus = SET_LAYER_LOAD_NOT_STARTED
          }

          // Assign the new layer to the draft layers object and return to `immer.produce`.
          layers[layerConf.titleid] = layerConf
        }
      })
    case SET_LAYER_VISIBLE:
      return produce(state, layers => {
        layers[action.titleid].visible = true
      })
    case SET_LAYER_HIDDEN:
      return produce(state, layers => {
        layers[action.titleid].visible = false
      })
    case SET_LAYER_ID:
      return produce(state, layers => {
        set(layers[action.titleid], 'config.spatial.id', action.id)
      })
    case SET_LAYER_IN_DRAWER_HIDDEN:
      return produce(state, layers => {
        layers[action.titleid].showInLayerControl = false
      })
    case SET_LAYER_IN_DRAWER_VISIBLE:
      return produce(state, layers => {
        layers[action.titleid].showInLayerControl = true
      })
    case SET_LAYER_SELECTED:
      return produce(state, layers => {
        layers[action.titleid].selected = true
      })
    case SET_LAYER_UNSELECTED:
      return produce(state, layers => {
        layers[action.titleid].selected = false
      })
    case SET_LAYER_PANELS_OPEN:
      return produce(state, layers => {
        layers[action.titleid].panelsOpen = true
      })
    case SET_LAYER_PANELS_CLOSED:
      return produce(state, layers => {
        layers[action.titleid].panelsOpen = false
      })
    case SET_LAYER_PANELS_FEATURE_OPEN:
      return produce(state, layers => {
        layers[action.titleid].panelFeatureTitle = cloneDeep(action.panelFeatureTitle)
      })
    case SET_LAYER_FILTER:
      return produce(state, layers => {
        layers[action.titleid].propsFilter = cloneDeep(action.propsFilter)
      })
    case SET_LAYER_REPORT:
      return produce(state, layers => {
        if (action.reportGroup.prop === '') {
          layers[action.titleid].report = false
        } else {
          layers[action.titleid].report = true
        }
        layers[action.titleid].reportGroup = cloneDeep(action.reportGroup)
      })
    case SET_LAYER_LOAD_NOT_STARTED:
      return produce(state, layers => {
        layers[action.titleid].loadstatus = SET_LAYER_LOAD_NOT_STARTED
      })
    case SET_LAYER_LOAD_IN_PROGRESS:
      return produce(state, layers => {
        if (layers[action.titleid]) {
          layers[action.titleid].loadstatus = SET_LAYER_LOAD_IN_PROGRESS
        } else {
          // Handle case where load status gets called before layer is registered. This should never happen!
          console.warn('redux SET_LAYER_LOAD_IN_PROGRESS error: layer not registered!', action.titleid, { ...layers })
        }
      })
    case SET_LAYER_LOAD_DONE:
      return produce(state, layers => {
        if (layers[action.titleid]) {
          layers[action.titleid].loadstatus = SET_LAYER_LOAD_DONE
        } else {
          // Handle case where load status gets called before layer is registered. This should never happen!
          console.warn('redux SET_LAYER_LOAD_IN_PROGRESS error: layer not registered!', action.titleid, { ...layers })
        }
      })
    case SET_LAYER_LOAD_FAILED:
      return produce(state, layers => {
        if (layers[action.titleid]) {
          layers[action.titleid].loadstatus = SET_LAYER_LOAD_FAILED
        } else {
          // Handle case where load status gets called before layer is registered. This should never happen!
          console.warn('redux SET_LAYER_LOAD_IN_PROGRESS error: layer not registered!', action.titleid, { ...layers })
        }
      })
    case SET_LAYER_STYLE_GEOSTYLER:
      return produce(state, layers => {
        // Force the style to be an array of styles as that's what the GeoStyler expects
        const styles = [].concat(action.style || [])
        const layer = layers[action.titleid]
        if (layer?.styleGeoStyler) {
          layer.styleGeoStyler.style = styles
        } else {
          console.warn('redux SET_LAYER_STYLE_GEOSTYLER - layer config not found!', action.titleid, layer, { ...layers })
        }
      })

    default:
      return state
  }
}
